import { SCHEMA_VERSION_HEADER } from '@oneappexchange/oaf-schema';
import axios, { AxiosAdapter, AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { produce } from 'immer';
import { DEFAULT_API_TIMEOUT } from '../config';
import { CORRELATION_ID_PARAM, randomUUID } from './cross_env_utils';
import { handleException } from './error_utilities';

// Add any headers you want to show up in the logs here, be careful not to add any headers that might expose PII
const LOGGED_HEADERS = ['Version', SCHEMA_VERSION_HEADER, CORRELATION_ID_PARAM];

export enum RequestType {
    GET = 'GET',
    POST = 'POST',
    PUT = 'PUT',
    PATCH = 'PATCH',
    DELETE = 'DELETE',
}

export interface HttpRequestConfig {
    url?: string;
    method?: RequestType;
    timeout?: number; //Time in milliseconds until a timeout error is returned
    data?: AxiosRequestConfig['data'];
    headers?: Record<string, string>;
    transformResponse?: AxiosRequestConfig['transformResponse'];
    params?: AxiosRequestConfig['params'];
    includeCorrelationIdHeader?: boolean;
    paramsSerializer?: AxiosRequestConfig['paramsSerializer'];
}

export const returnResponseData = (response: AxiosResponse) => {
    return response.data;
};

export const gatewayParamsSerializer = (params: Record<string, string | string[]>) => {
    return Object.entries(params)
        .map(([key, value]) => {
            const parsedValue = Array.isArray(value) ? value.join(',') : value;

            return key + '=' + encodeURIComponent(parsedValue);
        })
        .join('&');
};

class Http {
    _adapter: AxiosAdapter | undefined;

    // Please ONLY use this in TESTING
    // We need to override axios adapter, this is because Nock needs httpAdapter and jest is configure with jsdom so by default uses xhrAdapter
    set adapter(value: AxiosAdapter) {
        this._adapter = value; // this also works (but isn't clean): axios.defaults.adapter = value;
    }

    fetch(config: HttpRequestConfig) {
        const defaultConfig: Partial<AxiosRequestConfig> = {
            timeout: DEFAULT_API_TIMEOUT,
            // Include x-correlation-id header by default unless `includeCorrelationIdHeader` option is specifically set to false
            headers:
                config.includeCorrelationIdHeader !== false
                    ? {
                          [CORRELATION_ID_PARAM]: config.headers?.correlationId ?? randomUUID(),
                      }
                    : {},
        };

        const axiosConfig: AxiosRequestConfig = {
            url: config.url,
            method: config.method,
            timeout: config.timeout ?? defaultConfig.timeout,
            data: config.data,
            headers: { ...defaultConfig.headers, ...config.headers },
            transformResponse: config.transformResponse,
            params: config.params,
            paramsSerializer: config.paramsSerializer,
        };

        if (this._adapter) {
            axiosConfig.adapter = this._adapter;
        }

        return axios(axiosConfig).catch((error: AxiosError) => {
            const status = error?.response?.status;
            handleException(error, `http.fetch`, {
                status,
                statusText: error?.response?.statusText || undefined,
                reason: `Axios Error${status ? ` [${status}]` : ''}`,
                response: error?.response?.data,
                config: produce(axiosConfig as AxiosRequestConfig & { headers: AnyObject }, (draft) => {
                    // Strip fields that are not helpful for debugging
                    draft.timeout = undefined;
                    draft.transformResponse = undefined;
                    // Sanitize any headers that should not be logged
                    for (const key of Object.keys(draft.headers)) {
                        if (!LOGGED_HEADERS.includes(key)) {
                            draft.headers[key] = '*-hidden-*';
                        }
                    }
                }),
            });
            throw error;
        });
    }

    get(url: string, config: HttpRequestConfig = {}) {
        config.url = url;
        config.method = RequestType.GET;

        // TODO: Retry only for org based timeout errors

        return this.fetch(config);
    }

    post(url: string, config: HttpRequestConfig = {}) {
        config.url = url;
        config.method = RequestType.POST;
        return this.fetch(config);
    }

    put(url: string, config: HttpRequestConfig = {}) {
        config.url = url;
        config.method = RequestType.PUT;
        return this.fetch(config);
    }

    patch(url: string, config: HttpRequestConfig = {}) {
        config.url = url;
        config.method = RequestType.PATCH;
        return this.fetch(config);
    }

    delete(url: string, config: HttpRequestConfig = {}) {
        config.url = url;
        config.method = RequestType.DELETE;
        return this.fetch(config);
    }
}

export const http = new Http();
